import * as React from 'react';
import { useEffect, useState } from 'react';
import { useLocalization } from '@bumble/localization/components';
import { useRouter } from 'next/router';
import supportedLanguages, { SupportedLanguage } from 'lib/lexemes/supported-languages';
import { onUseCookieConsentEvent, onUsePrivacyManagerHandler } from 'lib/cookie-consent';
import JoinHivesPage from 'components/join-hives/join-hives-page';
import type { FooterLinksProps } from 'components/footer-links/footer-links';
import type { SocialLinksProps } from 'components/social-links/social-links';
import { App } from 'components/app/app';

type Props = {
    lang: SupportedLanguage;
    footerLinks: FooterLinksProps['items'];
    socialLinks: SocialLinksProps['socialLinks'];
    copyright: string;
};

const IndexPage: React.FC<Props> = function ({ lang, footerLinks, socialLinks, copyright }) {
    const Localization = useLocalization();
    const router = useRouter();
    const [isMobile, setIsMobile] = useState(false);
    const [ref, setRef] = useState<string | null>(null);

    useEffect(() => {
        setIsMobile(window.outerWidth < 800);

        function handleResize() {
            setIsMobile(window.outerWidth < 800);
        }

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (router.isReady && router.query.ref) {
            setRef(router.query?.ref as string);
        }
    }, [router.isReady, router.query.ref]);

    // configure cookie consent click handlers
    useEffect(onUseCookieConsentEvent, []);
    useEffect(onUsePrivacyManagerHandler, []);

    const title = Localization.get('bumblesite@join_hives.page_header');
    const text = Localization.get('bumblesite@join_hives.page_subcopy');
    const cta = Localization.get('bumblesite@join_hives.get_started_cta');
    const bullet1Header = Localization.get('bumblesite@join_hives.bullet_1.header');
    const bullet1Text = Localization.get('bumblesite@join_hives.bullet_1.text');
    const bullet2Header = Localization.get('bumblesite@join_hives.bullet_2.header');
    const bullet2Text = Localization.get('bumblesite@join_hives.bullet_2.text');
    const bullet3Header = Localization.get('bumblesite@join_hives.bullet_3.header');
    const bullet3Text = Localization.get('bumblesite@join_hives.bullet_3.text');

    return (
        <App>
            <JoinHivesPage
                isMobile={isMobile}
                title={title}
                text={text}
                bullets={[
                    // design team asked to swap bullets 1 and 2
                    { header: bullet2Header, text: bullet2Text },
                    { header: bullet1Header, text: bullet1Text },
                    { header: bullet3Header, text: bullet3Text },
                ]}
                cta={{
                    text: cta,
                    url: `https://bumble.com/aa/landto?ref=${ref}`,
                }}
                appStore={{
                    url: `https://apps.apple.com/${lang.toUpperCase()}/app/id930441707`,
                    text: Localization.get('bumblesite@join_hives.app_store_button'),
                }}
                playStore={{
                    url: 'https://play.google.com/store/apps/details?id=com.bumble.app',
                    text: Localization.get('bumblesite@join_hives.play_store_button'),
                }}
                footerLinks={footerLinks}
                socialLinks={socialLinks}
                copyright={copyright}
            />
        </App>
    );
};

export default IndexPage;

export { getStaticProps } from 'server/page-props';

export async function getStaticPaths() {
    return {
        paths: supportedLanguages.map((lang) => ({
            params: {
                lang,
            },
        })),
        fallback: false,
    };
}

// function escapeHTML(str: string) {
//     return str
//         .replace(/&/g, '&amp;')
//         .replace(/</g, '&lt;')
//         .replace(/>/g, '&gt;')
//         .replace(/"/g, '&quot;');
// }
